import React, { useCallback, useMemo, useState } from "react";
import { monitoringClient } from "src/services/requestClient";

const UserAnalysisContext = React.createContext({});

const UserAnalysisProvider = ({ children }) => {
  const [dataOverview, setDataOverview] = useState({});
  const [userActiveness, setUserActiveness] = useState({});
  const [nourTransactionTypes, setNourTransactionTypes] = useState({});
  const [nourTransactionVolume, setNourTransactionVolume] = useState({});
  const [topUsers, setTopUsers] = useState({});

  const [topActiveWallets, setTopActiveWallets] = useState({});

  const fetchDataUserAnalysisOverview = async (pool) => {
    if (dataOverview[pool]) return;
    try {
      const data = await monitoringClient.get(`/users/number-users?lending=${pool}`);
      let _data = dataOverview;
      _data[pool] = {
        totalUser: data.all_users,
        activeUser: data.active_users,
        justDepositUsers: data.just_deposit_users,
        borrowUsers: data.deposit_borrow_users,
        lazyUsers: data.lazy_users,
      };
      setDataOverview({ ..._data });
    } catch (error) {
      console.error(error);
    }
  };

  const _fetchUserActiveness = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/users/number-users-volatility", { params: _params });
      let activeUsers = [];
      let borrowingUsers = [];
      let depositOnlyUsers = [];
      const _data = Object.entries(data.data);
      for (var item of _data) {
        const timestamp = item[0] * 1000;
        const _activeUsers = item[1]["activeUsers"];
        const _borrowingUsers = item[1]["borrowingUsers"];
        const _depositOnlyUsers = item[1]["depositOnlyUsers"];
        activeUsers.push([timestamp, _activeUsers]);
        borrowingUsers.push([timestamp, _borrowingUsers]);
        depositOnlyUsers.push([timestamp, _depositOnlyUsers]);
      }
      let _tempData = userActiveness;
      _tempData[pool] = {
        activeUsers: activeUsers,
        borrowingUsers: borrowingUsers,
        depositOnlyUsers: depositOnlyUsers,
        metadata: params,
        active,
      };
      setUserActiveness({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUserActiveness = useCallback(async (pool, params, config) => {
    if (userActiveness[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = userActiveness[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchUserActiveness(pool, params, config?.active);
      }
    } else await _fetchUserActiveness(pool, params, config?.active);
  }, []);

  const _fetchNOURTransactionTypes = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      const data = await monitoringClient.get("/users/number-active-users", { params: _params });
      let borrow = [];
      let deposit = [];
      let repay = [];
      let withdraw = [];
      const _data = Object.entries(data);
      let index = 0;
      for (var item of _data) {
        const timestamp = item[0] * 1000;
        let _temp = `-${timestamp}`;
        if (index > 0) _temp = `${_data[index - 1][0] * 1000}-${timestamp}`;
        const _borrow = item[1]["borrow"];
        const _deposit = item[1]["deposit"];
        const _repay = item[1]["repay"];
        const _withdraw = item[1]["withdraw"];
        borrow.push({ x: timestamp, y: _borrow, id: _temp });
        deposit.push({ x: timestamp, y: _deposit, id: _temp });
        repay.push({ x: timestamp, y: _repay, id: _temp });
        withdraw.push({ x: timestamp, y: _withdraw, id: _temp });
        index++;
      }
      let _tempData = nourTransactionTypes;
      _tempData[pool] = {
        borrow: borrow,
        deposit: deposit,
        repay: repay,
        withdraw: withdraw,
        metadata: params,
        active,
      };
      setNourTransactionTypes({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchNOURTransactionTypes = useCallback(async (pool, params, config) => {
    if (nourTransactionTypes[pool]) {
      if (config?.isCheckMetadata != false) {
        const metadata = nourTransactionTypes[pool]["metadata"];
        if (metadata == params) return;
        else await _fetchNOURTransactionTypes(pool, params, config?.active);
      }
    } else await _fetchNOURTransactionTypes(pool, params, config?.active);
  }, []);

  const _fetchNOURTransactionVolume = async (pool) => {
    const data = await monitoringClient.get(`/users/user-histogram?lending=${pool}`);

    const rawDeposit = Object.entries(data.deposit);
    let _deposit = { categories: [], data: [] };
    rawDeposit.slice(1).forEach((element, index) => {
      _deposit.data.push(rawDeposit[index][1]);
      _deposit.categories.push(`${rawDeposit[index][0]}-${element[0]}`);
    });
    const lastDeposit = rawDeposit.slice(-1)[0];
    _deposit.data.push(lastDeposit[1]);
    _deposit.categories.push(`more than ${lastDeposit[0]}`);

    const rawBorrow = Object.entries(data.borrow);
    let _borrow = { categories: [], data: [] };
    rawBorrow.slice(1).forEach((element, index) => {
      _borrow.data.push(rawBorrow[index][1]);
      _borrow.categories.push(`${rawBorrow[index][0]}-${element[0]}`);
    });
    const lastBorrow = rawBorrow.slice(-1)[0];
    _borrow.data.push(lastBorrow[1]);
    _borrow.categories.push(`more than ${lastBorrow[0]}`);

    const rawTvl = Object.entries(data.tvl);
    let _tvl = { categories: [], data: [] };
    rawTvl.slice(1).forEach((element, index) => {
      _tvl.data.push(rawTvl[index][1]);
      _tvl.categories.push(`${rawTvl[index][0]}-${element[0]}`);
    });
    const lastTvl = rawTvl.slice(-1)[0];
    _tvl.data.push(lastTvl[1]);
    _tvl.categories.push(`more than ${lastTvl[0]}`);

    let _tempData = nourTransactionVolume;
    _tempData[pool] = {
      deposit: _deposit,
      borrow: _borrow,
      tvl: _tvl,
    };
    setNourTransactionVolume({ ..._tempData });
  };

  const fetchNOURTransactionVolume = useCallback(async (pool) => {
    if (!nourTransactionVolume[pool]) await _fetchNOURTransactionVolume(pool);
  }, []);

  const _fetchTopUsers = async (pool) => {
    try {
      const data = await monitoringClient.get(`/users/top-wallets?lending=${pool}`);

      const rawDeposit = data.deposit;
      let _deposit = { categories: [], data: [], maxUser: [], maxValue: 0 };
      rawDeposit.forEach((element) => {
        _deposit.categories.push(element["wallet"]);
        _deposit.data.push(element["valueInUSD"]);
        if (_deposit.maxValue === element["valueInUSD"]) _deposit.maxUser.push(element["wallet"]);
        else if (_deposit.maxValue < element["valueInUSD"]) {
          _deposit.maxUser = [element["wallet"]];
          _deposit.maxValue = [element["valueInUSD"]];
        }
      });

      const rawBorrow = data.borrow;
      let _borrow = { categories: [], data: [], maxUser: [], maxValue: 0 };
      rawBorrow.forEach((element) => {
        _borrow.categories.push(element["wallet"]);
        _borrow.data.push(element["valueInUSD"]);
        if (_borrow.maxValue === element["valueInUSD"]) _borrow.maxUser.push(element["wallet"]);
        else if (_borrow.maxValue < element["valueInUSD"]) {
          _borrow.maxUser = [element["wallet"]];
          _borrow.maxValue = [element["valueInUSD"]];
        }
      });

      const rawTvl = data.tvl;
      let _tvl = { categories: [], data: [], maxUser: [], maxValue: 0 };
      rawTvl.forEach((element) => {
        _tvl.categories.push(element["wallet"]);
        _tvl.data.push(element["valueInUSD"]);
        if (_tvl.maxValue === element["valueInUSD"]) _tvl.maxUser.push(element["wallet"]);
        else if (_tvl.maxValue < element["valueInUSD"]) {
          _tvl.maxUser = [element["wallet"]];
          _tvl.maxValue = element["valueInUSD"];
        }
      });

      let _tempData = topUsers;
      _tempData[pool] = {
        deposit: _deposit,
        borrow: _borrow,
        tvl: _tvl,
      };
      setTopUsers({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTopUsers = useCallback(async (pool) => {
    if (!topUsers[pool]) await _fetchTopUsers(pool);
  }, []);

  const fetchTopActiveWallets = async (pool) => {
    if (setTopActiveWallets[pool]) return setTopActiveWallets[pool];
    try {
      const data = await monitoringClient.get(`/users/top-active-wallets?lending=${pool}`);
      let _tempData = topActiveWallets;
      _tempData[pool] = {
        all: data.all,
        borrow: data.borrow,
        deposit: data.deposit,
      };
      setTopActiveWallets({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const contextData = useMemo(() => {
    return {
      dataOverview,
      topActiveWallets,
      chart: {
        userActiveness,
        nourTransactionTypes,
        nourTransactionVolume,
        topUsers,
      },
      fetch: {
        fetchDataUserAnalysisOverview,
        fetchUserActiveness,
        fetchNOURTransactionTypes,
        fetchTopActiveWallets,
        fetchNOURTransactionVolume,
        fetchTopUsers,
      },
    };
  }, [dataOverview, topActiveWallets, userActiveness, nourTransactionTypes, nourTransactionVolume, topUsers]);

  return <UserAnalysisContext.Provider value={contextData}>{children}</UserAnalysisContext.Provider>;
};

export default UserAnalysisProvider;

export const useUserAnalysisContext = () => React.useContext(UserAnalysisContext);

export const useUserAnalysisSelector = (selectorFn) => {
  const data = useUserAnalysisContext();

  return useMemo(() => selectorFn(data), [data, selectorFn]);
};

export const useUserAnalysisChartSelector = (chartType, pool) => {
  const { chart } = useUserAnalysisContext();
  return useMemo(() => {
    return chart[chartType][pool];
  }, [chart, chartType, pool]);
};
